import { useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Ticket, Search, X, ChevronRight, CheckCircle2 } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { formatCurrency, formatDateTime } from '../lib/utils'

// Ops-mediated event-voucher redemption. Used when a recipient walks
// up (venue / phone) with an unredeemed 8-char code but can't or
// won't redeem it themselves in the DrinX app. Operator looks up the
// voucher by code, verifies the story, then enters the customer's
// phone → ticket is minted under that customer's account.
//
// Backend: /admin/vouchers list, /admin/vouchers/:code detail,
// /admin/vouchers/:code/redeem-for-user for the actual mint.

type VoucherStatus = 'active' | 'redeemed' | 'cancelled' | 'expired'

interface VoucherListRow {
  id: string
  code: string
  status: VoucherStatus
  quantity: number
  amount_fils: number
  payment_method: 'card' | 'store_credit'
  recipient_phone: string | null
  recipient_email: string | null
  gift_message: string | null
  expires_at: string
  redeemed_at: string | null
  created_at: string
  event_name_en: string
  event_starts_at: string
  event_ends_at: string | null
  buyer_name: string | null
  buyer_phone: string | null
}

interface VoucherDetail extends VoucherListRow {
  event_id: string
  event_location: string
  event_capacity: number
  event_time_mode: 'none' | 'display' | 'pick'
  event_daily_open_time: string | null
  event_daily_close_time: string | null
  tier_id: string | null
  tier_name_en: string | null
  tier_name_ar: string | null
  redeemed_by_user_id: string | null
  redeemer_name: string | null
  redeemer_phone: string | null
}

const STATUS_LABELS: Record<VoucherStatus, string> = {
  active: 'Active',
  redeemed: 'Redeemed',
  cancelled: 'Cancelled',
  expired: 'Expired',
}
const STATUS_COLORS: Record<VoucherStatus, string> = {
  active: 'bg-emerald-500/15 text-emerald-400',
  redeemed: 'bg-gray-500/15 text-gray-400',
  cancelled: 'bg-red-500/15 text-red-400',
  expired: 'bg-yellow-500/15 text-yellow-400',
}

export default function Vouchers() {
  const { can } = useAuth()
  const canRedeem = can(['super_admin', 'operations'])
  const [status, setStatus] = useState<VoucherStatus | ''>('active')
  const [search, setSearch] = useState('')
  const [detailCode, setDetailCode] = useState<string | null>(null)

  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (search.trim()) params.set('search', search.trim())

  const { data, isLoading } = useQuery<{ vouchers: VoucherListRow[] }>({
    queryKey: ['vouchers', status, search],
    queryFn: () => api.get(`/vouchers?${params.toString()}`).then(r => r.data),
  })

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Ticket size={24} className="text-[#C41E3A]" />
          <h1 className="text-2xl font-bold text-white">Event vouchers</h1>
        </div>
      </div>

      <p className="text-sm text-gray-400 mb-5 max-w-2xl">
        Ops-mediated redemption for customers who can't redeem their gift voucher in the DrinX app themselves.
        Search by code, phone, or email; click a row to redeem for a customer by phone.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Code, phone, or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#C41E3A]"
          />
        </div>
        <select
          value={status}
          onChange={e => setStatus(e.target.value as VoucherStatus | '')}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C41E3A]"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="redeemed">Redeemed</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading…</div>
        ) : (data?.vouchers?.length ?? 0) === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">No vouchers match.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-400 uppercase tracking-wider border-b border-white/5">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Code</th>
                <th className="text-left px-4 py-3 font-semibold">Event</th>
                <th className="text-left px-4 py-3 font-semibold">Buyer</th>
                <th className="text-left px-4 py-3 font-semibold">Recipient</th>
                <th className="text-right px-4 py-3 font-semibold">Qty</th>
                <th className="text-right px-4 py-3 font-semibold">Value</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Created</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {data!.vouchers.map(v => (
                <tr
                  key={v.id}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                  onClick={() => setDetailCode(v.code)}
                >
                  <td className="px-4 py-3 font-mono text-white font-semibold tracking-wider">{v.code}</td>
                  <td className="px-4 py-3 text-white max-w-xs truncate">{v.event_name_en}</td>
                  <td className="px-4 py-3 text-gray-300">
                    {v.buyer_name ?? '—'}
                    <div className="text-xs text-gray-500">{v.buyer_phone ?? ''}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {v.recipient_phone ?? v.recipient_email ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-right text-white tabular-nums">{v.quantity}</td>
                  <td className="px-4 py-3 text-right text-white tabular-nums">
                    {formatCurrency(v.amount_fils)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${STATUS_COLORS[v.status]}`}>
                      {STATUS_LABELS[v.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{formatDateTime(v.created_at)}</td>
                  <td className="px-4 py-3 text-gray-500">
                    <ChevronRight size={16} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detailCode && (
        <VoucherDetailDrawer
          code={detailCode}
          canRedeem={canRedeem}
          onClose={() => setDetailCode(null)}
        />
      )}
    </div>
  )
}

function VoucherDetailDrawer({
  code, canRedeem, onClose,
}: {
  code: string
  canRedeem: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [showRedeem, setShowRedeem] = useState(false)

  const { data, isLoading } = useQuery<{ voucher: VoucherDetail }>({
    queryKey: ['voucher', code],
    queryFn: () => api.get(`/vouchers/${code}`).then(r => r.data),
  })

  const v = data?.voucher

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="fixed inset-0 bg-black/50" />
      <div
        className="relative w-full max-w-md bg-[#151515] border-l border-white/10 overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#151515] border-b border-white/10 flex items-center justify-between px-5 py-4 z-10">
          <div className="flex items-center gap-3">
            <span className="font-mono text-white font-bold text-lg tracking-wider">{code}</span>
            {v && (
              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${STATUS_COLORS[v.status]}`}>
                {STATUS_LABELS[v.status]}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {isLoading || !v ? (
          <div className="p-6 text-gray-500 text-sm">Loading…</div>
        ) : (
          <div className="p-5 space-y-6">
            <Section label="Event">
              <div className="text-white font-semibold">{v.event_name_en}</div>
              <div className="text-xs text-gray-400 mt-1">
                {v.event_ends_at === null ? 'Ongoing experience' : formatDateTime(v.event_starts_at)}
                {' · '}{v.event_location}
              </div>
              {v.tier_name_en && (
                <div className="text-xs text-gray-500 mt-1">Tier: {v.tier_name_en}</div>
              )}
            </Section>

            <Section label="Details">
              <Row k="Quantity" v={String(v.quantity)} />
              <Row k="Paid" v={`${formatCurrency(v.amount_fils)} · ${v.payment_method === 'card' ? 'Card' : 'Store credit'}`} />
              <Row k="Expires" v={formatDateTime(v.expires_at)} />
              <Row k="Created" v={formatDateTime(v.created_at)} />
            </Section>

            <Section label="Buyer">
              <Row k="Name" v={v.buyer_name ?? '—'} />
              <Row k="Phone" v={v.buyer_phone ?? '—'} />
            </Section>

            <Section label="Recipient">
              <Row k="Phone" v={v.recipient_phone ?? '—'} />
              <Row k="Email" v={v.recipient_email ?? '—'} />
              {v.gift_message && (
                <div className="mt-2 text-xs text-gray-300 italic border-l-2 border-white/10 pl-3">
                  "{v.gift_message}"
                </div>
              )}
            </Section>

            {v.status === 'redeemed' && (
              <Section label="Redemption">
                <Row k="Claimed at" v={v.redeemed_at ? formatDateTime(v.redeemed_at) : '—'} />
                <Row k="By" v={`${v.redeemer_name ?? '—'} (${v.redeemer_phone ?? '—'})`} />
              </Section>
            )}

            {v.status === 'active' && canRedeem && (
              <div className="pt-2">
                <button
                  onClick={() => setShowRedeem(true)}
                  className="w-full py-3 rounded-lg bg-[#C41E3A] hover:bg-[#A81830] text-white font-semibold text-sm flex items-center justify-center gap-2"
                >
                  <CheckCircle2 size={16} />
                  Redeem for a customer
                </button>
              </div>
            )}
          </div>
        )}

        {showRedeem && v && (
          <RedeemForCustomerModal
            voucher={v}
            onClose={() => setShowRedeem(false)}
            onSuccess={() => {
              setShowRedeem(false)
              qc.invalidateQueries({ queryKey: ['voucher', code] })
              qc.invalidateQueries({ queryKey: ['vouchers'] })
            }}
          />
        )}
      </div>
    </div>
  )
}

function RedeemForCustomerModal({
  voucher, onClose, onSuccess,
}: {
  voucher: VoucherDetail
  onClose: () => void
  onSuccess: () => void
}) {
  const [phone, setPhone] = useState('')
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [time, setTime] = useState('')
  const [error, setError] = useState<string | null>(null)

  const isOngoing = voucher.event_ends_at === null
  const needsTime = voucher.event_time_mode === 'pick'

  const redeem = useMutation({
    mutationFn: () => {
      const body: Record<string, string> = { user_phone: phone.trim() }
      if (isOngoing) body.attendance_date = date
      if (needsTime && time) body.attendance_time = time
      return api.post(`/vouchers/${voucher.code}/redeem-for-user`, body).then(r => r.data)
    },
    onSuccess: () => onSuccess(),
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } } }
      setError(err.response?.data?.message ?? 'Redeem failed')
    },
  })

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="fixed inset-0 bg-black/70" />
      <div
        className="relative w-full max-w-md bg-[#151515] border border-white/10 rounded-2xl p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">Redeem for customer</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <p className="text-xs text-gray-400 mb-5 leading-relaxed">
          Mints a ticket for <span className="text-white font-semibold">{voucher.event_name_en}</span>
          {' '}under the customer's DrinX account. They must already have an account (sign up in the app first).
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
              Customer phone
            </label>
            <input
              type="tel"
              placeholder="+962 79 000 0000"
              value={phone}
              onChange={e => { setPhone(e.target.value); setError(null) }}
              className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#C41E3A]"
            />
            <p className="text-[10px] text-gray-500 mt-1">Matches the last 9 digits, so ±962 prefix doesn't matter.</p>
          </div>

          {isOngoing && (
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                Attendance date
              </label>
              <input
                type="date"
                value={date}
                onChange={e => { setDate(e.target.value); setError(null) }}
                min={new Date().toISOString().slice(0, 10)}
                className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C41E3A]"
              />
            </div>
          )}

          {needsTime && (
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                Attendance time
              </label>
              <input
                type="time"
                value={time}
                onChange={e => { setTime(e.target.value); setError(null) }}
                min={voucher.event_daily_open_time ?? undefined}
                max={voucher.event_daily_close_time ?? undefined}
                className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#C41E3A]"
              />
              {voucher.event_daily_open_time && (
                <p className="text-[10px] text-gray-500 mt-1">
                  Window: {voucher.event_daily_open_time} – {voucher.event_daily_close_time}
                </p>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-300">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3 mt-5">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-white/10 text-gray-300 hover:bg-white/5 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={() => redeem.mutate()}
            disabled={!phone.trim() || redeem.isPending}
            className="flex-1 py-2.5 rounded-lg bg-[#C41E3A] hover:bg-[#A81830] disabled:opacity-50 text-white font-semibold text-sm"
          >
            {redeem.isPending ? 'Redeeming…' : 'Confirm redeem'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{label}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-gray-500 text-xs">{k}</span>
      <span className="text-white text-right ml-3">{v}</span>
    </div>
  )
}
